import { Box, TextField, Typography, useTheme, Paper, Autocomplete } from '@mui/material';
import { useEffect, useState, FC, ReactNode, useCallback } from 'react';
import { IPlatePackConfiguration } from '../../../../Models/IPlatePackConfiguration';
import { Constants } from '../../../../Models/Constants';
import { createFlexStyles } from '../../../../theme/layoutStyles';

interface IFormValidation {
    config?: IPlatePackConfiguration,
    pheType?: string
}

interface NumberInputProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    required?: boolean;
    disabled?: boolean;
    styles: any;
}

const NumberInput: FC<NumberInputProps> = ({ label, value, onChange, required = false, disabled = false, styles }) => {
    return (
        <Box sx={styles.numberInputContainer}>
            <Typography {...styles.numberInputLabel}>{label}</Typography>
            <TextField
                {...styles.numberInputField}
                required={required}
                disabled={disabled}
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value)}
            />
        </Box>
    );
};

interface TypeSelectorProps {
    label: string;
    value: string;
    options: {key: string, text: string, disabled?: boolean}[];
    onChange: (value: string) => void;
    required?: boolean;
    disabled?: boolean;
    styles: any;
}

const TypeSelector: FC<TypeSelectorProps> = ({ label, value, options, onChange, required = false, disabled = false, styles }) => {
    return (
        <Box sx={styles.typeSelectorContainer}>
            <Typography {...styles.typeSelectorLabel}>{label}</Typography>
            <Autocomplete
                size="small"
                disabled={disabled}
                options={options}
                getOptionLabel={(option) => option.text}
                getOptionDisabled={(option) => !!option.disabled}
                value={options.find(o => o.text === value) || null}
                onChange={(_, newValue) => {
                    onChange(newValue ? newValue.text : '');
                }}
                openOnFocus
                autoHighlight
                isOptionEqualToValue={(option, val) => option.text === val.text}
                renderInput={(params) => (
                    <TextField 
                        {...params} 
                        required={required}
                    />
                )}
                sx={styles.typeSelectorFormControl}
            />
        </Box>
    );
};

interface OperatorProps {
    symbol: string;
    styles: any;
}

const Operator: FC<OperatorProps> = ({ symbol, styles }) => {
    return (
        <Box sx={styles.operatorContainer}>
            <Typography {...styles.operatorText}>{symbol}</Typography>
        </Box>
    );
};

interface ConfigRowProps {
    style: { [key: string]: any };
    children: ReactNode;
}

const ConfigRow: FC<ConfigRowProps> = ({ style, children }) => {
    return (
        <Box sx={{
            ...style,
            pt: 2,
            pb: 2, 
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap',
        }}>
            {children}
        </Box>
    );
};

    // PHE Type options
    const PHE_TYPE_OPTIONS: {key: string, text: string, disabled?: boolean}[] = [
        { key: 'H', text: 'H' },
        { key: 'L', text: 'L' },
        { key: 'LNa', text: 'LNa' },
        { key: 'LWi', text: 'LWi' },
        { key: 'M', text: 'M' },
        { key: 'MH', text: 'MH' },
        { key: 'ML', text: 'ML' },
        { key: 'HG', text: 'HG' },
        { key: 'HW', text: 'HW' },
        { key: 'LG', text: 'LG' },
        { key: 'LW', text: 'LW' },
        { key: 'MG', text: 'MG' },
        { key: 'MW', text: 'MW' },
    ];

/**
 * PHE Type Plate Pack Input Form component to display the form for PHE type and plate pack configuration input. 
 * Material UI version.
 *
 * @param {*} { setConfig, setPheType, validateForm, shouldValidateFormInputs, pheTypes, existingConfig, existingPheType, isReadOnly, label }
 * @return {*} 
 */
export const PheTypePlatePackInputForm: React.FC<{
    setConfig: (config: IPlatePackConfiguration) => void,
    setPheType: (pheType: string) => void,
    validateForm: (valid: boolean) => void,
    shouldValidateFormInputs?: boolean,
    pheTypes?: {key: string, text: string}[],
    existingConfig?: IPlatePackConfiguration,
    existingPheType?: string,
    isReadOnly?: boolean,
    label?: string,
}> = (props) => {
    // Basic state setup
    const theme = useTheme();
    const flexStyles = createFlexStyles(theme);
    const [grouping, setGrouping] = useState<string>(props.existingConfig?.type ?? 'Basic');
    const [readOnly, setReadOnly] = useState(props.isReadOnly ?? false);
    const [platePackConfig, setPlatePackConfig] = useState<IPlatePackConfiguration>({ type: props.existingConfig?.type ?? 'Basic' });
    const [pheT, setPheT] = useState(props.existingPheType ?? '');

    /**
     * Filters the type options based on the current value of the plate pack configuration.
     *
     * @param {IPlatePackConfiguration} currentValue
     */
    const filterTypeOptions = useCallback((currentValue: IPlatePackConfiguration) => {
        // if ML is picked on one side, only MH is allowed on the other and vice versa.
        // Always start from fresh options to avoid stale state issues
        let currentOptions = PHE_TYPE_OPTIONS.map(opt => ({...opt}));
        
        if (currentValue.cold_plate_pattern_type_1 === 'ML'
            || currentValue.cold_plate_pattern_type_2 === 'ML'
            || currentValue.hot_plate_pattern_type_2 === 'ML'
            || currentValue.hot_plate_pattern_type_1 === 'ML'
        ) {
            const mhOption = currentOptions.find(x => x.key === 'MH');
            if (mhOption) mhOption.disabled = true;
        }

        if (currentValue.cold_plate_pattern_type_1 === 'MH'
            || currentValue.cold_plate_pattern_type_2 === 'MH'
            || currentValue.hot_plate_pattern_type_2 === 'MH'
            || currentValue.hot_plate_pattern_type_1 === 'MH'
        ) {
            const mlOption = currentOptions.find(x => x.key === 'ML');
            if (mlOption) mlOption.disabled = true;
        }
        
        setTypeOptions(currentOptions);
    }, []);

    // Consolidated styles object
    const styles = {
        // Main container styles
        paper: {
            border: '0px'
        },
        titleContainer: {
            marginBottom: theme.spacing(3)
        },
        titleText: {
            fontWeight: 'medium',
            gutterBottom: true
        },
        subtitleText: {
            color: 'text.secondary',
            marginBottom: theme.spacing(1)
        },
        configTypeText: {
            color: 'text.secondary'
        },

        // Form controls
        formControl: {
            fullWidth: true,
            size: 'small' as const
        },
        selectPlaceholder: {
            disabled: true,
            value: ''
        },

        // Configuration type selection grid
        configGrid: {
            display: 'flex',
            flexWrap: 'wrap',
            // justifyContent: 'space-between',
            marginBottom: theme.spacing(3),
            marginTop: theme.spacing(1),
            // width: '90%',
            '& > .MuiBox-root': { 
                flex: 1,
                mx: 0.5
            }
        },

        // Configuration selection boxes
        configBox: (isSelected: boolean) => ({
            ...flexStyles.flexCenter,
            // flex: 'unset',
            padding: '10px',
            borderRadius: theme.shape.borderRadius,
            margin: theme.spacing(0.5),
            cursor: !readOnly ? 'pointer' : 'default',
            transition: 'all 0.2s ease',
            // minHeight: 80,
            ...(isSelected ? {
                border: `2px solid ${theme.palette.primary.main}`,
                boxShadow: `0 0 4px ${theme.palette.primary.main}`
            } : {
                border: '1px solid',
                borderColor: 'divider'
            }),
            '&:hover': !readOnly ? { 
                bgcolor: 'action.hover',
                transform: 'translateY(-2px)',
            } : {}
        }),

        // Visual indicator dots
        redDot: {
            bgcolor: 'error.main',
            height: 12,
            width: 12,
            borderRadius: '4px'
        },
        blueDot: {
            bgcolor: 'primary.main',
            height: 12,
            width: 12,
            borderRadius: '4px'
        },
        spacerDot: {
            height: 2,
            width: 2,
            bgcolor: 'background.paper'
        },

        // Configuration row styles
        warmConfigRow: {
            ...flexStyles.flexCenter,
            border: `2px solid ${theme.palette.error.main}`,
            borderRadius: theme.shape.borderRadius,
            marginBottom: theme.spacing(1),
            padding: theme.spacing(1),
            paddingTop: theme.spacing(2),
            paddingBottom: theme.spacing(2),
            flexWrap: 'nowrap'
        },
        coldConfigRow: {
            ...flexStyles.flexCenter,
            border: `2px solid ${theme.palette.primary.main}`,
            borderRadius: theme.spacing(1),
            padding: theme.spacing(1),
            paddingTop: theme.spacing(2),
            paddingBottom: theme.spacing(2),
            flexWrap: 'nowrap'
        },

        // Input component styles
        numberInputContainer: {
            display: 'flex',
            flexDirection: 'column' as const,
            mx: 1,
            flex: 1
        },
        numberInputLabel: {
            variant: 'caption' as const,
            fontWeight: 'medium'
        },
        numberInputField: {
            required: false,
            disabled: false,
            type: 'number' as const,
            size: 'small' as const,
            sx: {
                ml: '1px',
                minWidth: '40px',
                '& .MuiOutlinedInput-root': {
                    height: '35px', // Match standard small TextField height
                },
                '& input[type=number]': {
                    '-moz-appearance': 'textfield'
                },
                '& input[type=number]::-webkit-outer-spin-button': {
                    '-webkit-appearance': 'none',
                    margin: 0
                },
                '& input[type=number]::-webkit-inner-spin-button': {
                    '-webkit-appearance': 'none',
                    margin: 0
                },
            },
            inputProps: { 
                style: { 
                    textAlign: 'center',
                    paddingLeft: '2px',
                    paddingRight: '2px'
                } 
            }
        },

        // Type selector styles
        typeSelectorContainer: {
            display: 'flex',
            flexDirection: 'column' as const,
            flex: 1,
            mx: 1
        },
        typeSelectorLabel: {
            variant: 'caption' as const,
            fontWeight: 'medium'
        },
        typeSelectorFormControl: {
            // fullWidth: true,
            size: 'small' as const,
            minWidth: '75px',
            '& .MuiOutlinedInput-root': {
                paddingLeft: '2px',
                paddingRight: '22px !important',
                height: '35px', // Match standard small TextField height
            },
            '& .MuiAutocomplete-input': {
                textAlign: 'center',
                padding: '0px !important',
            }
        },

        // Operator styles
        operatorContainer: {
            ...flexStyles.flexCenter,
            mx: 1,
        },
        operatorText: {
            variant: 'h6' as const
        },

        // Visual arrangement styles
        dotRow: {
            ...flexStyles.flexCenterGap1,
            gap: theme.spacing(0.8) // Custom gap override
        },
        dotColumn: {
            marginTop: theme.spacing(0.8)
        }
    };

    // PHE Type options moved outside component

    const [typeOptions, setTypeOptions] = useState<{key: string, text: string, disabled?: boolean}[]>(PHE_TYPE_OPTIONS);

    useEffect(() => {
        if (props.existingConfig) {
            setPlatePackConfig(props.existingConfig ?? {});
            setReadOnly(props.isReadOnly ?? false);
            setGrouping(props.existingConfig.type ?? 'Basic');
            setPheT(props.existingPheType ?? '');
            props.setPheType(props.existingPheType ?? '');
        }
        else {
            validateInputs({ config: platePackConfig, pheType: pheT });
        }

        setReadOnly(props.isReadOnly ?? false);
        setPheT(props.existingPheType ?? '');
        props.setPheType(props.existingPheType ?? '');
    }, [props.existingConfig, filterTypeOptions]);

    const changeGrouping = (type: string) => {
        if (readOnly) return;

        let plateP: IPlatePackConfiguration = { ...platePackConfig, type: type };
        validateInputs({ config: plateP, pheType: pheT });
        setPlatePackConfig(plateP);
        setGrouping(type);
        props.validateForm(false); // Reset form validation.
    };

    /**
     * Validates the form inputs and sets the form validation state. 
     *
     * @param {IFormValidation} formData
     */
    const validateInputs = (formData: IFormValidation) => {
        if (!readOnly) {
            let config = formData.config;
            let dropDownsValid = false;
            if (config?.type === Constants.VaryingTheta) {
                dropDownsValid =
                    (config.hot_plate_pattern_type_1 !== undefined && config.hot_plate_pattern_type_1 !== '')
                    && (config.hot_plate_pattern_type_2 !== undefined && config.hot_plate_pattern_type_2 !== '')
                    && (config.cold_plate_pattern_type_1 !== undefined && config.cold_plate_pattern_type_1 !== '')
                    && (config.cold_plate_pattern_type_2 !== undefined && config.cold_plate_pattern_type_2 !== ''
                        && (config.cold_1_channels_per_pass_type_1 !== undefined && config.cold_1_channels_per_pass_type_1 !== ''));
            } else if (config?.type === Constants.VaryingChannelsPasses) {
                dropDownsValid =
                    config.hot_plate_pattern_type_1 !== undefined && config.hot_plate_pattern_type_1 !== ''
                    && config.cold_plate_pattern_type_1 !== undefined && config.cold_plate_pattern_type_1 !== '';
            }
            else {
                dropDownsValid =
                    config?.hot_plate_pattern_type_1 !== undefined && config?.hot_plate_pattern_type_1 !== ''
                    && config?.cold_plate_pattern_type_1 !== undefined && config?.cold_plate_pattern_type_1 !== '';
            }

            if (props.shouldValidateFormInputs && document.forms[0] !== undefined) {
                let formValid = document.forms[0].checkValidity();
                props.validateForm(dropDownsValid && formData.pheType !== undefined && formData.pheType !== '' && formValid);
            }
            else {
                props.validateForm(dropDownsValid && formData.pheType !== undefined && formData.pheType !== '');
            }
        }
    };

    /**
     * Handles the change event for the input fields. 
     *
     * @param {string} input - The new value
     * @param {string} target - The field to update
     */
    const handleChange = (input: string, target: string) => {
        let currentValue: IPlatePackConfiguration = platePackConfig;
        let isPhe: boolean = false;

        switch (target) {
            case 'hot_1_passes':
                currentValue = { ...currentValue, hot_1_passes: input, type: grouping }
                setPlatePackConfig(currentValue);
                break;
            case 'hot_1_channels_per_pass_type_1':
                currentValue = { ...currentValue, hot_1_channels_per_pass_type_1: input, type: grouping }
                setPlatePackConfig(currentValue);
                break;
            case 'hot_1_channels_per_pass_type_2':
                currentValue = { ...currentValue, hot_1_channels_per_pass_type_2: input, type: grouping }
                setPlatePackConfig(currentValue);
                break;
            case 'hot_2_passes':
                currentValue = { ...currentValue, hot_2_passes: input, type: grouping }
                setPlatePackConfig(currentValue);
                break;
            case 'hot_2_channels_per_pass':
                currentValue = { ...currentValue, hot_2_channels_per_pass: input, type: grouping }
                setPlatePackConfig(currentValue);
                break;
            case 'hot_plate_pattern_type_1':
                currentValue = { ...currentValue, hot_plate_pattern_type_1: input, type: grouping }
                setPlatePackConfig(currentValue);
                break;
            case 'hot_plate_pattern_type_2':
                currentValue = { ...currentValue, hot_plate_pattern_type_2: input, type: grouping }
                setPlatePackConfig(currentValue);
                break;
            case 'cold_1_passes':
                currentValue = { ...currentValue, cold_1_passes: input, type: grouping }
                setPlatePackConfig(currentValue);
                break;
            case 'cold_1_channels_per_pass_type_1':
                currentValue = { ...currentValue, cold_1_channels_per_pass_type_1: input, type: grouping }
                setPlatePackConfig(currentValue);
                break;
            case 'cold_1_channels_per_pass_type_2':
                currentValue = { ...currentValue, cold_1_channels_per_pass_type_2: input, type: grouping }
                setPlatePackConfig(currentValue);
                break;
            case 'cold_2_passes':
                currentValue = { ...currentValue, cold_2_passes: input, type: grouping }
                setPlatePackConfig(currentValue);
                break;
            case 'cold_2_channels_per_pass':
                currentValue = { ...currentValue, cold_2_channels_per_pass: input, type: grouping }
                setPlatePackConfig(currentValue);
                break;
            case 'cold_plate_pattern_type_1':
                currentValue = { ...currentValue, cold_plate_pattern_type_1: input, type: grouping }
                setPlatePackConfig(currentValue);
                break;
            case 'cold_plate_pattern_type_2':
                currentValue = { ...currentValue, cold_plate_pattern_type_2: input, type: grouping }
                setPlatePackConfig(currentValue);
                break;

            case 'pheType':
                setPheT(input);
                props.setPheType(props.pheTypes!.find(x => x.key.toString() === input)!.text);
                isPhe = true;
                break;
            default:
                break;
        }

        props.setConfig(currentValue);
        if (isPhe) {
            validateInputs({ config: currentValue, pheType: input });
        }
        else {
            validateInputs({ config: currentValue, pheType: pheT });
        }
    };
    
    return (
        <Paper elevation={0} sx={styles.paper}>
            <Box sx={styles.titleContainer}>
                <Typography variant="subtitle1" {...styles.titleText}>
                    {props.label ?? 'Plate pack configuration'}
                </Typography>
                
                <Typography {...styles.subtitleText}>Select PHE Type</Typography>
                <Autocomplete
                    size="small"
                    disabled={readOnly}
                    options={props.pheTypes || []}
                    getOptionLabel={(option) => option.text}
                    value={props.pheTypes?.find(o => o.key === pheT) || null}
                    onChange={(_, newValue) => {
                        handleChange(newValue ? newValue.key : '', 'pheType');
                    }}
                    openOnFocus
                    autoHighlight
                    renderInput={(params) => (
                        <TextField 
                            {...params} 
                            required
                            placeholder="Select PHE Type"
                        />
                    )}
                />
            </Box>
            
            <Typography {...styles.configTypeText}>
                Configuration Type: <Box component="span" sx={{fontWeight: 'medium'}}>{grouping}</Box>
            </Typography>
            
            {/* Configuration type selection */}
            <Box sx={styles.configGrid}>
                {/* Basic selection */}
                <Box 
                    sx={styles.configBox(grouping === 'Basic')}
                    onClick={() => changeGrouping('Basic')}
                >
                    <Box>
                        <Box sx={styles.dotRow}>
                            <Box sx={styles.redDot}></Box>
                            <Box sx={styles.redDot}></Box>
                            <Box sx={styles.redDot}></Box>
                        </Box>
                        <Box sx={{...styles.dotRow, ...styles.dotColumn}}>
                            <Box sx={styles.blueDot}></Box>
                            <Box sx={styles.blueDot}></Box>
                            <Box sx={styles.blueDot}></Box>
                        </Box>
                    </Box>
                </Box>

                {/* Varying theta */}
                <Box 
                    sx={styles.configBox(grouping === 'Varying theta')}
                    onClick={() => changeGrouping('Varying theta')}
                >
                    <Box>
                        <Box sx={styles.dotRow}>
                            <Box sx={styles.redDot}></Box>
                            <Box sx={styles.redDot}></Box>
                            <Box sx={styles.redDot}></Box>
                            <Box sx={styles.spacerDot}></Box>
                            <Box sx={styles.redDot}></Box>
                            <Box sx={styles.redDot}></Box>
                        </Box>
                        <Box sx={{...styles.dotRow, ...styles.dotColumn}}>
                            <Box sx={styles.blueDot}></Box>
                            <Box sx={styles.blueDot}></Box>
                            <Box sx={styles.blueDot}></Box>
                            <Box sx={styles.spacerDot}></Box>
                            <Box sx={styles.blueDot}></Box>
                            <Box sx={styles.blueDot}></Box>
                        </Box>
                    </Box>
                </Box>

                {/* Varying channels in passes */}
                <Box 
                    sx={styles.configBox(grouping === 'Varying channels in passes')}
                    onClick={() => changeGrouping('Varying channels in passes')}
                >
                    <Box>
                        <Box sx={styles.dotRow}>
                            <Box sx={styles.redDot}></Box>
                            <Box sx={styles.redDot}></Box>
                            <Box sx={styles.redDot}></Box>
                            <Box sx={styles.redDot}></Box>
                            <Box sx={styles.redDot}></Box>
                        </Box>
                        <Box sx={{...styles.dotRow, ...styles.dotColumn}}>
                            <Box sx={styles.blueDot}></Box>
                            <Box sx={styles.blueDot}></Box>
                            <Box sx={styles.blueDot}></Box>
                            <Box sx={styles.blueDot}></Box>
                            <Box sx={styles.blueDot}></Box>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Basic configuration */}
            {grouping === 'Basic' && (
                <Box>
                    {/* Hot side configuration */}
                    <ConfigRow style={styles.warmConfigRow}>
                        <NumberInput
                            label="Pass"
                            value={platePackConfig.hot_1_passes ?? ''}
                            onChange={(value) => handleChange(value, 'hot_1_passes')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                        <Operator symbol="×" styles={styles} />
                        <NumberInput
                            label="Chan"
                            value={platePackConfig.hot_1_channels_per_pass_type_1 ?? ''}
                            onChange={(value) => handleChange(value, 'hot_1_channels_per_pass_type_1')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                        <TypeSelector
                            label="Type"
                            value={platePackConfig.hot_plate_pattern_type_1 ?? ''}
                            options={typeOptions}
                            onChange={(value) => handleChange(value, 'hot_plate_pattern_type_1')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                    </ConfigRow>

                    {/* Cold side configuration */}
                    <ConfigRow style={styles.coldConfigRow}>
                        <NumberInput
                            label="Pass"
                            value={platePackConfig.cold_1_passes ?? ''}
                            onChange={(value) => handleChange(value, 'cold_1_passes')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                        <Operator symbol="×" styles={styles} />
                        <NumberInput
                            label="Chan"
                            value={platePackConfig.cold_1_channels_per_pass_type_1 ?? ''}
                            onChange={(value) => handleChange(value, 'cold_1_channels_per_pass_type_1')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                        <TypeSelector
                            label="Type"
                            value={platePackConfig.cold_plate_pattern_type_1 ?? ''}
                            options={typeOptions}
                            onChange={(value) => handleChange(value, 'cold_plate_pattern_type_1')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                    </ConfigRow>
                </Box>
            )}

            {/* Varying theta configuration */}
            {grouping === 'Varying theta' && (
                <Box>
                    {/* Hot side configuration */}
                    <ConfigRow style={styles.warmConfigRow}>
                        <NumberInput
                            label="Pass1"
                            value={platePackConfig.hot_1_passes ?? ''}
                            onChange={(value) => handleChange(value, 'hot_1_passes')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                        <Operator symbol="×" styles={styles} />
                        <NumberInput
                            label="Chan1"
                            value={platePackConfig.hot_1_channels_per_pass_type_1 ?? ''}
                            onChange={(value) => handleChange(value, 'hot_1_channels_per_pass_type_1')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                        <TypeSelector
                            label="Type1"
                            value={platePackConfig.hot_plate_pattern_type_1 ?? ''}
                            options={typeOptions}
                            onChange={(value) => handleChange(value, 'hot_plate_pattern_type_1')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                        <Operator symbol="+" styles={styles} />
                        <NumberInput
                            label="Chan2"
                            value={platePackConfig.hot_1_channels_per_pass_type_2 ?? ''}
                            onChange={(value) => handleChange(value, 'hot_1_channels_per_pass_type_2')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                        <TypeSelector
                            label="Type2"
                            value={platePackConfig.hot_plate_pattern_type_2 ?? ''}
                            options={typeOptions}
                            onChange={(value) => handleChange(value, 'hot_plate_pattern_type_2')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                    </ConfigRow>

                    {/* Cold side configuration */}
                    <ConfigRow style={styles.coldConfigRow}>
                        <NumberInput
                            label="Pass1"
                            value={platePackConfig.cold_1_passes ?? ''}
                            onChange={(value) => handleChange(value, 'cold_1_passes')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                        <Operator symbol="×" styles={styles} />
                        <NumberInput
                            label="Chan1"
                            value={platePackConfig.cold_1_channels_per_pass_type_1 ?? ''}
                            onChange={(value) => handleChange(value, 'cold_1_channels_per_pass_type_1')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                        <TypeSelector
                            label="Type1"
                            value={platePackConfig.cold_plate_pattern_type_1 ?? ''}
                            options={typeOptions}
                            onChange={(value) => handleChange(value, 'cold_plate_pattern_type_1')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                        <Operator symbol="+" styles={styles} />
                        <NumberInput
                            label="Chan2"
                            value={platePackConfig.cold_1_channels_per_pass_type_2 ?? ''}
                            onChange={(value) => handleChange(value, 'cold_1_channels_per_pass_type_2')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                        <TypeSelector
                            label="Type2"
                            value={platePackConfig.cold_plate_pattern_type_2 ?? ''}
                            options={typeOptions}
                            onChange={(value) => handleChange(value, 'cold_plate_pattern_type_2')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                    </ConfigRow>
                </Box>
            )}

            {/* Varying channels in passes configuration */}
            {grouping === 'Varying channels in passes' && (
                <Box>
                    {/* Hot side configuration */}
                    <ConfigRow style={styles.warmConfigRow}>
                        <NumberInput
                            label="Pass1"
                            value={platePackConfig.hot_1_passes ?? ''}
                            onChange={(value) => handleChange(value, 'hot_1_passes')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                        <Operator symbol="×" styles={styles} />
                        <NumberInput
                            label="Chan1"
                            value={platePackConfig.hot_1_channels_per_pass_type_1 ?? ''}
                            onChange={(value) => handleChange(value, 'hot_1_channels_per_pass_type_1')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                        <Operator symbol="+" styles={styles} />
                        <NumberInput
                            label="Pass2"
                            value={platePackConfig.hot_2_passes ?? ''}
                            onChange={(value) => handleChange(value, 'hot_2_passes')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                        <Operator symbol="×" styles={styles} />
                        <NumberInput
                            label="Chan2"
                            value={platePackConfig.hot_2_channels_per_pass ?? ''}
                            onChange={(value) => handleChange(value, 'hot_2_channels_per_pass')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                        <TypeSelector
                            label="Type1"
                            value={platePackConfig.hot_plate_pattern_type_1 ?? ''}
                            options={typeOptions}
                            onChange={(value) => handleChange(value, 'hot_plate_pattern_type_1')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                    </ConfigRow>

                    {/* Cold side configuration */}
                    <ConfigRow style={styles.coldConfigRow}>
                        <NumberInput
                            label="Pass1"
                            value={platePackConfig.cold_1_passes ?? ''}
                            onChange={(value) => handleChange(value, 'cold_1_passes')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                        <Operator symbol="×" styles={styles} />
                        <NumberInput
                            label="Chan1"
                            value={platePackConfig.cold_1_channels_per_pass_type_1 ?? ''}
                            onChange={(value) => handleChange(value, 'cold_1_channels_per_pass_type_1')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                        <Operator symbol="+" styles={styles} />
                        <NumberInput
                            label="Pass2"
                            value={platePackConfig.cold_2_passes ?? ''}
                            onChange={(value) => handleChange(value, 'cold_2_passes')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                        <Operator symbol="×" styles={styles} />
                        <NumberInput
                            label="Chan2"
                            value={platePackConfig.cold_2_channels_per_pass ?? ''}
                            onChange={(value) => handleChange(value, 'cold_2_channels_per_pass')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                        <TypeSelector
                            label="Type1"
                            value={platePackConfig.cold_plate_pattern_type_1 ?? ''}
                            options={typeOptions}
                            onChange={(value) => handleChange(value, 'cold_plate_pattern_type_1')}
                            required
                            disabled={readOnly}
                            styles={styles}
                        />
                    </ConfigRow>
                </Box>
            )}
        </Paper>
    );
}; 